// Голямата търсачка на работния лист „Инвентаризация" — същият падащ списък
// като при „Доставка": Продукт (highlight) · Търсено (наличност към датата) ·
// Ед. цена. Изборът на продукт НЕ добавя ред (целият каталог вече е в
// таблицата) — той СКАЧА с фокуса в „Намерено" на продукта. Търсят се и
// РЕЦЕПТИТЕ (маркер „Рецепта", както в изписването): изборът добавя
// приготвената рецепта в „Приготвени рецепти" (или скача до нея, ако вече е
// там) с фокус в „Порции". Празен резултат предлага „Нов продукт „…“" с
// написаното име. Клавишите са в общия `useWorksheetSearch`.
import { useEffect } from "react";
import { Input } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Product } from "../../../../api/inventory";
import { useInventoryShell } from "../../components/shell/InventoryShellContext";
import { formatUnitPrice } from "../../components/numbers";
import {
  DROPDOWN_GRID_CLASS,
  DROPDOWN_GRID_NARROW_CLASS,
  formatQtyInput,
  highlightWords,
} from "./stocktakeWorksheetShared";

// Един ред от резултатите: продукт (търсеното/цената/дали е преброен) или
// рецепта (дали вече е в „Приготвени рецепти")
export type StocktakeSearchRow =
  | {
      kind: "product";
      product: Product;
      counted: boolean;
      systemQty: number;
      unitPrice: number | null;
      decimals: number;
    }
  | { kind: "recipe"; recipeId: number; name: string; added: boolean };

type Props = {
  attachInputRef: (node: HTMLInputElement | null) => void;
  value: string;
  onValueChange: (value: string) => void;
  onFocusChange: (focused: boolean) => void;
  onInputKeyDown: (e: React.KeyboardEvent) => void;
  dropdownOpen: boolean;
  rows: StocktakeSearchRow[];
  searchWords: string[];
  highlightedIndex: number;
  onHighlightChange: (index: number) => void;
  onPick: (row: StocktakeSearchRow) => void;
  // Продуктът липсва в справочника → създава се направо оттук (org базата)
  onCreateProduct: () => void;
};

// Маркерите в реда: „Рецепта" и „✓ преброен" / „✓ добавена"
const TAG_CLASS =
  "shrink-0 rounded-full border border-brand-tint2 bg-brand-tint px-[9px] pb-[3px] pt-[2px] text-[11px] font-semibold leading-[normal] text-brand-deep-ink";

export const StocktakeWorksheetSearch = ({
  attachInputRef,
  value,
  onValueChange,
  onFocusChange,
  onInputKeyDown,
  dropdownOpen,
  rows,
  searchWords,
  highlightedIndex,
  onHighlightChange,
  onPick,
  onCreateProduct,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  // На телефон — без колоната „Ед. цена" (името иначе не се събира)
  const gridClass = isNarrow ? DROPDOWN_GRID_NARROW_CLASS : DROPDOWN_GRID_CLASS;

  // Маркираният резултат винаги остава във видимата част на списъка
  useEffect(() => {
    if (!dropdownOpen) return;
    document
      .getElementById(`stx-opt-${highlightedIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, dropdownOpen]);

  // Колоните на продукт: име · търсено · ед. цена; на рецепта — име и маркер
  const renderCells = (row: StocktakeSearchRow) => {
    if (row.kind === "recipe")
      return (
        <>
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="truncate text-[15px] font-semibold leading-[normal] text-ink">
              {highlightWords(row.name, searchWords)}
            </span>
            <span className={TAG_CLASS}>{t("inventoryPage.recipe")}</span>
            {row.added && <span className={TAG_CLASS}>✓ {t("inventoryPage.sr6SearchAdded")}</span>}
          </div>
          <div className="whitespace-nowrap text-right text-[13.5px] leading-[normal] text-ghost">—</div>
          {!isNarrow && <div />}
        </>
      );
    return (
      <>
        {/* ПРОДУКТ */}
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="truncate text-[15px] font-semibold leading-[normal] text-ink">
            {highlightWords(row.product.name, searchWords)}
          </span>
          {row.product.unit && (
            <span className="shrink-0 text-xs leading-[normal] text-muted">{row.product.unit}</span>
          )}
          {row.counted && <span className={TAG_CLASS}>✓ {t("inventoryPage.stxCountedTag")}</span>}
        </div>
        {/* ТЪРСЕНО */}
        <div className="whitespace-nowrap text-right font-sheet text-[13.5px] font-semibold leading-[normal] text-body">
          {formatQtyInput(row.systemQty, row.decimals)}
          {row.product.unit ? (
            <span className="font-sans text-xs font-normal text-muted"> {row.product.unit}</span>
          ) : null}
        </div>
        {/* ЕД. ЦЕНА */}
        {!isNarrow && (
          <div
            className={`whitespace-nowrap text-right font-sheet text-[15px] font-bold leading-[normal] ${
              row.unitPrice != null ? "text-brand-deep-ink" : "text-ghost"
            }`}
          >
            {row.unitPrice != null ? formatUnitPrice(row.unitPrice) : "—"}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="relative shrink-0">
      <Input
        ref={(node) => attachInputRef(node?.input ?? null)}
        allowClear
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onFocus={() => onFocusChange(true)}
        onBlur={() => onFocusChange(false)}
        onKeyDown={onInputKeyDown}
        prefix={
          <SearchOutlined className="mr-1 text-[17px] text-muted" />
        }
        placeholder={t("inventoryPage.sr6SearchPh")}
        // ! — antd cssinjs е извън Tailwind layer-ите и иначе бие класа;
        // 16px на телефон — под 16px iOS Safari зуумва страницата при фокус
        className={`w-full !rounded-[10px] ${isNarrow ? "!text-base" : "!text-[15px]"}`}
      />
      {dropdownOpen && (
        <div className="dk-elevated absolute inset-x-0 top-[calc(100%+6px)] z-30 overflow-hidden rounded-xl border border-shade-3 bg-surface shadow-[0_18px_44px_rgba(20,15,8,0.16),0_4px_12px_rgba(20,15,8,0.08)]">
          <div
            className={`${gridClass} items-center gap-[18px] border-b border-shade-2 bg-fill px-[18px] py-2 text-[10.5px] font-semibold uppercase leading-[normal] tracking-[0.07em] text-muted`}
          >
            <span className="truncate">
              {t("inventoryPage.wsResultsCount", { count: rows.length })}
            </span>
            <span className="whitespace-nowrap text-right">
              {t("inventoryPage.colSystem")}
            </span>
            {!isNarrow && (
              <span className="whitespace-nowrap text-right">
                {t("inventoryPage.colUnitPrice")}
              </span>
            )}
          </div>
          <div className="max-h-[min(46vh,430px)] overflow-y-auto">
            {rows.map((row, index) => (
              <div
                key={row.kind === "recipe" ? `r${row.recipeId}` : row.product.id}
                id={`stx-opt-${index}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPick(row)}
                onMouseMove={() => onHighlightChange(index)}
                className={`${gridClass} cursor-pointer items-center gap-[18px] border-b border-shade-1 px-[18px] py-[11px] ${
                  index === highlightedIndex ? "bg-brand-tint" : "bg-surface"
                }`}
              >
                {renderCells(row)}
              </div>
            ))}
            {rows.length === 0 && (
              // „Няма го“ → „Нов продукт“ с написаното име (както при доставка).
              // `onMouseDown` с preventDefault пази фокуса на търсачката — иначе
              // blur-ът затваря списъка преди click-а.
              <div className="flex flex-wrap items-center gap-3 px-4 py-3.5 text-[12.5px] leading-[normal] text-muted">
                {t("inventoryPage.wsNoMatch")}
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onCreateProduct}
                  className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-shade-4 bg-transparent px-2.5 py-1.5 text-[12.5px] font-semibold text-ink hover:border-brand hover:text-brand-deep-ink"
                >
                  <PlusOutlined className="text-[11px]" />
                  {t("inventoryPage.wsCreateNamed", { name: value.trim() })}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StocktakeWorksheetSearch;
